import {
  clearFeedbackRestore,
  readFeedbackRestore,
  routeRestoresFeedbackView
} from "@appretio/feedback";
import { Icon, IconButton } from "@appretio/ui-kit";
import { useEffect, useState } from "react";
import { useLocation, useMatches, useNavigate } from "react-router-dom";
import {
  noticeBase,
  noticeBody,
  noticeText
} from "./FeedbackRestoreNotice.css";

/** What the notice says, pinned to the page it landed on. */
interface ShownNotice {
  reporter: string;
  screenshotCount: number;
  pathname: string;
}

/**
 * Tells the user when a feedback restore lands on a page that does not
 * hydrate from it. RestoreFeedbackPage forwards EVERY report onto its page
 * with the payload in router state, but only pages whose route `handle`
 * carries `RESTORES_FEEDBACK_VIEW_HANDLE` read it. Anywhere else the page
 * opens at its defaults and the restore would look like it silently did
 * nothing — so this says so instead.
 *
 * Rendered once in the app layout. It takes the payload off the router
 * state (so a refresh or Back doesn't repeat the notice), keeps it while
 * the user stays on that pathname — the page's own search-param updates,
 * such as a tab switch, don't drop it — and goes away on dismiss or when
 * the user navigates to another page.
 */
export function FeedbackRestoreNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const matches = useMatches();
  const [notice, setNotice] = useState<ShownNotice | null>(null);

  const restore = readFeedbackRestore(location);
  const pageHydrates = matches.some((match) =>
    routeRestoresFeedbackView(match.handle)
  );

  const { pathname, search, hash } = location;
  useEffect(() => {
    if (!restore || pageHydrates) return;
    setNotice({
      reporter: restore.created_by,
      screenshotCount: restore.screenshot_count,
      pathname
    });
    clearFeedbackRestore(navigate, { pathname, search, hash });
  }, [restore, pageHydrates, pathname, search, hash, navigate]);

  // Left the page: drop the notice (adjusting own state during render, so
  // coming back later never resurrects it).
  if (notice && notice.pathname !== pathname) {
    setNotice(null);
  }
  if (!notice || notice.pathname !== pathname) return null;

  return (
    <div className={noticeBase} role="status">
      <Icon name="info" />
      <div className={noticeBody}>
        <p className={noticeText}>
          Opened {notice.reporter}&apos;s feedback report on this page, but
          restoring their view isn&apos;t supported here yet: only the page and
          its URL (such as the tab) were opened, not their selections or open
          dialogs.
        </p>
        {notice.screenshotCount > 0 && (
          <p className={noticeText}>
            The report&apos;s {notice.screenshotCount} screenshot(s) show what
            they saw.
          </p>
        )}
      </div>
      <IconButton
        variant="tertiary"
        size="xs"
        iconName="close"
        aria-label="Dismiss restore notice"
        onClick={() => setNotice(null)}
      />
    </div>
  );
}
